import React, { useState } from 'react';

export function evidenceLabel(item, manifest) {
  const source = (manifest || []).find((entry) => entry.source_id === item?.source_id);
  const name = source?.filename || item?.source_id || 'source';
  return item?.page ? `${name}, page ${item.page}` : name;
}

export function reviewEntityCards(entities) {
  return (entities || []).filter((entity) => (
    entity
    && entity.provenance !== 'visually_observed'
    && entity.review_status !== 'rejected'
  ));
}

export function photoGroups(manifest) {
  return (manifest || []).filter((item) => item?.source_type === 'image' && (item.people || []).length);
}

export function peopleCountLabel(count) {
  const total = Number(count) || 0;
  return `about ${total} ${total === 1 ? 'person' : 'people'} detected`;
}

function Field({ label, value, onChange, testId }) {
  return (
    <label className="block text-[10px] text-[#9CA3AF]">
      {label}
      <input
        data-testid={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
      />
    </label>
  );
}

export function StoryContextReview({
  summary,
  manifest,
  entities,
  claims,
  questions,
  versionId,
  reconfirmNotice,
  confirmRequested,
  busy,
  onPatch,
  onLock,
  mode = 'full',
}) {
  const cards = reviewEntityCards(entities);
  const characters = cards.filter((entity) => entity.entity_type !== 'place');
  const photos = photoGroups(manifest);
  const conflicts = (claims || []).filter((claim) => claim.conflict_status === 'conflicting');
  const [names, setNames] = useState({});
  const [added, setAdded] = useState('');
  const [splits, setSplits] = useState({});
  const [mergeInto, setMergeInto] = useState({});
  const [personNames, setPersonNames] = useState({});
  const [personLinks, setPersonLinks] = useState({});
  const [peopleCounts, setPeopleCounts] = useState({});
  const [adjusting, setAdjusting] = useState(false);
  const brief = mode === 'brief';
  const showEditor = !brief || adjusting;

  return (
    <div className="space-y-3" data-testid="story-context-review">
      <div data-testid="story-context-summary">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">{brief ? 'Does this match?' : 'What Twinnee understood'}</p>
        <p className="text-xs text-[#E5E7EB] mt-1">{summary || 'The summary will appear when reading finishes.'}</p>
        {brief && characters.length ? (
          <p className="text-[11px] text-[#9CA3AF] mt-2" data-testid="brief-mentions">
            Mentioned, not cast yet: {characters.map((entity) => entity.display_name).join(', ')}
          </p>
        ) : null}
      </div>
      {reconfirmNotice ? (
        <p className="rounded-xl border border-amber-400/40 bg-amber-950/40 p-3 text-xs text-amber-100" data-testid="reconfirm-notice">
          {reconfirmNotice}
        </p>
      ) : null}
      {confirmRequested && !brief ? (
        <p className="text-xs text-[#E5E7EB]" data-testid="confirm-requested">
          A chat message asked to confirm this version. The button below is still required.
        </p>
      ) : null}
      {photos.length ? (
        <div className="space-y-2" data-testid="people-in-photo">
          {photos.map((photo) => (
            <details key={photo.source_id} className="rounded-xl border border-white/10 p-2">
              <summary className="text-xs text-white cursor-pointer">
                People in photo · {photo.filename || 'image'} · {peopleCountLabel((photo.people || []).length)}
              </summary>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-1.5">
                  <label className="text-[10px] text-[#9CA3AF]" htmlFor={`people-count-${photo.source_id}`}>
                    Correct the count
                  </label>
                  <input
                    id={`people-count-${photo.source_id}`}
                    type="number"
                    min="0"
                    max="200"
                    aria-label="Correct the people count"
                    value={peopleCounts[photo.source_id] ?? (photo.user_people_count ?? (photo.people || []).length)}
                    onChange={(event) => setPeopleCounts((prev) => ({ ...prev, [photo.source_id]: event.target.value }))}
                    className="w-16 bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white"
                  />
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onPatch?.({
                      op: 'set_people_count',
                      source_id: photo.source_id,
                      people_count: Number(peopleCounts[photo.source_id] ?? photo.user_people_count ?? (photo.people || []).length),
                    })}
                    className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer"
                  >
                    Save count
                  </button>
                  {photo.user_people_count != null ? (
                    <span className="text-[10px] text-[#9CA3AF]" data-testid="people-count-correction">You counted {photo.user_people_count}.</span>
                  ) : null}
                </div>
                {(photo.people || []).map((person, index) => (
                  <div key={`${photo.source_id}-${index}`} className="rounded-lg border border-white/10 p-2 space-y-1.5" data-testid="photo-person">
                    <p className="text-xs text-[#E5E7EB]">{person.description || person.label || 'A person'}</p>
                    {person.decision ? <p className="text-[10px] text-[#9CA3AF]">{person.decision}</p> : null}
                    <div className="flex flex-wrap gap-1.5">
                      <select
                        aria-label="Link to a character"
                        value={personLinks[`${photo.source_id}:${index}`] || ''}
                        onChange={(event) => setPersonLinks((prev) => ({ ...prev, [`${photo.source_id}:${index}`]: event.target.value }))}
                        className="bg-[#0c1018] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white"
                      >
                        <option value="">Link to a character</option>
                        {characters.map((entity) => (
                          <option key={entity.entity_id} value={entity.entity_id}>{entity.display_name}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={busy || !personLinks[`${photo.source_id}:${index}`]}
                        onClick={() => onPatch?.({
                          op: 'link_person',
                          link: 'existing',
                          source_id: photo.source_id,
                          person_index: index,
                          entity_id: personLinks[`${photo.source_id}:${index}`],
                        })}
                        className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer disabled:opacity-40"
                      >
                        Link
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onPatch?.({
                          op: 'link_person',
                          link: 'ignore',
                          source_id: photo.source_id,
                          person_index: index,
                        })}
                        className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer"
                      >
                        Ignore
                      </button>
                    </div>
                    <div className="flex gap-1.5">
                      <input
                        placeholder="Someone else"
                        value={personNames[`${photo.source_id}:${index}`] || ''}
                        onChange={(event) => setPersonNames((prev) => ({ ...prev, [`${photo.source_id}:${index}`]: event.target.value }))}
                        className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white"
                      />
                      <button
                        type="button"
                        disabled={busy || !(personNames[`${photo.source_id}:${index}`] || '').trim()}
                        onClick={() => onPatch?.({
                          op: 'link_person',
                          link: 'new',
                          source_id: photo.source_id,
                          person_index: index,
                          display_name: personNames[`${photo.source_id}:${index}`],
                        })}
                        className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer disabled:opacity-40"
                      >
                        Mark as someone else
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          ))}
        </div>
      ) : null}
      {brief && !adjusting ? (
        <button
          type="button"
          onClick={() => setAdjusting(true)}
          className="text-[11px] text-[#9CA3AF] underline cursor-pointer"
          data-testid="brief-adjust"
        >
          Change names
        </button>
      ) : null}
      {showEditor ? (
      <div className="space-y-2" data-testid="story-context-entities">
        <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">Entities</p>
        {cards.map((entity) => (
          <div key={entity.entity_id} className="rounded-xl border border-white/10 p-2 space-y-1.5" data-testid="entity-card">
            <p className="text-xs text-white">{entity.display_name}</p>
            <p className="text-[10px] text-[#9CA3AF]">
              {entity.entity_type || 'character'} · {entity.provenance || 'unknown'} · {entity.review_status || 'proposed'}
              {entity.confidence != null ? ` · ${entity.confidence}` : ''}
            </p>
            {entity.description ? <p className="text-[11px] text-[#E5E7EB]">{entity.description}</p> : null}
            <ul className="text-[10px] text-[#9CA3AF]">
              {(entity.source_evidence || []).map((item, index) => (
                <li key={`${entity.entity_id}-ev-${index}`}>{evidenceLabel(item, manifest)}</li>
              ))}
            </ul>
            <Field
              label="Name"
              value={names[entity.entity_id] ?? entity.display_name}
              onChange={(value) => setNames((prev) => ({ ...prev, [entity.entity_id]: value }))}
            />
            <div className="flex flex-wrap gap-1.5">
              <button type="button" disabled={busy} onClick={() => onPatch?.({ op: 'edit', entity_id: entity.entity_id, display_name: names[entity.entity_id] ?? entity.display_name })} className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer">Edit</button>
              <button type="button" disabled={busy} onClick={() => onPatch?.({ op: 'confirm', entity_id: entity.entity_id })} className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer">Confirm</button>
              <button type="button" disabled={busy} onClick={() => onPatch?.({ op: 'reject', entity_id: entity.entity_id })} className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer">Reject</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <select
                aria-label={`Merge ${entity.display_name}`}
                value={mergeInto[entity.entity_id] || ''}
                onChange={(event) => setMergeInto((prev) => ({ ...prev, [entity.entity_id]: event.target.value }))}
                className="bg-[#0c1018] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white"
              >
                <option value="">Merge into</option>
                {cards.filter((other) => other.entity_id !== entity.entity_id).map((other) => (
                  <option key={other.entity_id} value={other.entity_id}>{other.display_name}</option>
                ))}
              </select>
              <button
                type="button"
                disabled={busy || !mergeInto[entity.entity_id]}
                onClick={() => onPatch?.({ op: 'merge', entity_id: entity.entity_id, merge_into: mergeInto[entity.entity_id] })}
                className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer disabled:opacity-40"
              >
                Merge
              </button>
              <input
                placeholder="Split name"
                value={splits[entity.entity_id] || ''}
                onChange={(event) => setSplits((prev) => ({ ...prev, [entity.entity_id]: event.target.value }))}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-[10px] text-white"
              />
              <button
                type="button"
                disabled={busy || !(splits[entity.entity_id] || '').trim()}
                onClick={() => onPatch?.({ op: 'split', entity_id: entity.entity_id, display_name: splits[entity.entity_id] })}
                className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer disabled:opacity-40"
              >
                Split
              </button>
            </div>
          </div>
        ))}
        <div className="flex gap-1.5">
          <input
            placeholder="Add an entity"
            value={added}
            onChange={(event) => setAdded(event.target.value)}
            className="flex-1 bg-white/[0.04] border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
          />
          <button
            type="button"
            disabled={busy || !added.trim()}
            onClick={() => {
              onPatch?.({ op: 'add', display_name: added.trim(), entity_type: 'character' });
              setAdded('');
            }}
            className="text-[10px] px-2 py-1 rounded-full border border-white/15 text-white cursor-pointer disabled:opacity-40"
          >
            Add
          </button>
        </div>
      </div>
      ) : null}
      {conflicts.length ? (
        <div data-testid="context-conflicts" className="space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-amber-200">Conflicts</p>
          {conflicts.map((claim) => (
            <p key={claim.claim_id} className="text-xs text-amber-100">
              {claim.predicate}: {typeof claim.value === 'string' ? claim.value : JSON.stringify(claim.value)}
              <button
                type="button"
                className="ml-2 underline cursor-pointer"
                onClick={() => onPatch?.({ claim_id: claim.claim_id, user_decision: 'keep' })}
              >
                Keep this
              </button>
            </p>
          ))}
        </div>
      ) : null}
      {(questions || []).length ? (
        <div data-testid="context-questions">
          <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">Questions</p>
          {questions.map((question) => (
            <p key={question} className="text-xs text-[#E5E7EB] mt-1">{question}</p>
          ))}
        </div>
      ) : null}
      {brief ? null : (
        <p className="text-[10px] text-[#9CA3AF]" data-testid="context-version-id">Version {versionId || 'not saved yet'}</p>
      )}
      <button
        type="button"
        disabled={Boolean(busy) || !versionId}
        onClick={onLock}
        className="w-full py-2.5 rounded-xl bg-[#2DD4BF] text-[#06080D] text-xs font-bold cursor-pointer disabled:opacity-60"
        data-testid="unified-story-lock"
      >
        {busy === 'confirm' ? 'Saving…' : (brief ? 'Yes, continue' : 'Confirm and lock story context')}
      </button>
    </div>
  );
}

export default StoryContextReview;
